import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Row,
  Form,
  DatePicker,
  Input,
  Checkbox,
} from 'antd';
import Select from 'components/Select';
import PaginatedSelect from 'components/PaginatedSelect';
import { HideToPreserve } from 'components/Steps';
import { getCategoriasDeQuejas, getMotivosDeQuejas } from 'api/solicitudes/quejas';
import { getPlantillasNoIndexadas } from 'api/tramites';
import { getDependencias } from 'api/configuracion';

const PlantillasSelect = ({
  label,
  name,
  labelProp,
  onChange,
  required,
}) => {
  const [data, setData] = useState({ results: [] });
  const [loading, setLoading] = useState(false);
  const plantillasRef = useRef();

  const fetchPlantillas = async (page = 1) => {
    setLoading(true);
    const response = await getPlantillasNoIndexadas({ page });
    setLoading(false);
    setData(response);
  };

  return (
    <Form.Item
      label={label}
      name={name}
      rules={[{ required }]}
    >
      <PaginatedSelect
        loading={loading}
        onChange={onChange}
        onClear={() => fetchPlantillas()}
        dataSource={data?.results?.length ? data : plantillasRef.current}
        labelProp={labelProp}
        pagination={{
          getter: async (...args) => {
            setLoading(true);
            const _data = await getPlantillasNoIndexadas({
              ...args[0],
              [labelProp]: args[0].q,
            });
            setLoading(false);
            return _data;
          },
          setter: (dataSource) => {
            plantillasRef.current = dataSource;
          },
          config: { auto: true },
        }}
      />
    </Form.Item>
  );
};

PlantillasSelect.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  labelProp: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
};

PlantillasSelect.defaultProps = {
  label: 'Plantillas',
  name: 'plantilla',
  labelProp: 'nombre',
  onChange: () => null,
  required: true,
};

const UnidadesOperativasSelect = ({
  label,
  name,
  labelProp,
  onChange,
  required,
}) => {
  const [data, setData] = useState({ results: [] });
  const [loading, setLoading] = useState(false);
  const unidadesOperativasRef = useRef();

  const fetchUnidadesOperativas = async (page = 1) => {
    setLoading(true);
    const response = await getDependencias({ page });
    setLoading(false);
    setData(response);

    return response;
  };

  return (
    <Form.Item
      label={label}
      name={name}
      rules={[{ required }]}
    >
      <PaginatedSelect
        loading={loading}
        onChange={onChange}
        onClear={() => fetchUnidadesOperativas()}
        dataSource={data?.results?.length ? data : unidadesOperativasRef.current}
        labelProp={labelProp}
        pagination={{
          getter: async (...args) => {
            setLoading(true);
            const _data = await getDependencias({
              ...args[0],
              [labelProp]: args[0].q,
            });
            setLoading(false);
            return _data;
          },
          setter: (dataSource) => {
            unidadesOperativasRef.current = dataSource;
          },
          config: { auto: true },
        }}
      />
    </Form.Item>
  );
};

UnidadesOperativasSelect.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  labelProp: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
};

UnidadesOperativasSelect.defaultProps = {
  label: 'Unidades Operativas',
  name: 'unidad_operativa',
  labelProp: 'nombre',
  onChange: () => null,
  required: true,
};

const InformacionGeneral = ({ visible }) => {
  const [motivos, setMotivos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [filteredMotivos, setFilteredMotivos] = useState();
  const [otroTramite, setOtroTramite] = useState(false);
  const [otraOficina, setOtraOficina] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      const [_motivos, _categorias] = await Promise.all([
        getMotivosDeQuejas(),
        getCategoriasDeQuejas(),
      ]);
      if (mounted) {
        setMotivos(_motivos);
        setCategorias(_categorias);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, []);

  const rules = {
    required: [{ required: true }],
  };

  const onChangeCategoria = (value) => {
    if (!value) {
      setFilteredMotivos([]);
      return;
    }
    const filtered = motivos.filter((motivo) => motivo.categoria_de_la_queja === value);
    setFilteredMotivos(filtered);
  };

  return (
    <HideToPreserve visible={visible}>
      <Row gutter={10}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="Tipo de protesta"
            name="categoria_de_la_queja"
            rules={rules.required}
          >
            <Select options={categorias} onChange={onChangeCategoria} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="Motivo de la queja"
            name="motivo_de_la_queja"
            rules={rules.required}
          >
            <Select options={filteredMotivos} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="Fecha de los hechos"
            name="fecha_de_los_hechos"
            rules={rules.required}
          >
            <DatePicker
              disabledDate={(current) => current.isAfter(new Date())}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="Hora aproximada de los hechos"
            name="hora_de_los_hechos"
            rules={rules.required}
          >
            <DatePicker.TimePicker placeholder="" use12Hours format="h:mm:ss a" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12} md={16}>
          <PlantillasSelect
            label="Tramite/Servicio que quería realizar"
            name="plantilla_de_tramites"
            required={!otroTramite}
          />
        </Col>
        <Col xs={12} sm={12} md={8}>
          <Form.Item
            label=" "
            name="otro_tramite"
          >
            <Checkbox
              checked={otroTramite}
              onChange={(e) => setOtroTramite(e.target.checked)}
            >
              Otro Trámite/Servicio
            </Checkbox>
          </Form.Item>
        </Col>
        {otroTramite && (
          <Col span={24}>
            <Form.Item
              label="Ingrese el nombre del trámite o servicio que quería realizar"
              name="tramite_de_queja"
              rules={rules.required}
            >
              <Input />
            </Form.Item>
          </Col>
        )}
        <Col xs={12} sm={12} md={16}>
          <UnidadesOperativasSelect
            label="Oficina en donde sucedieron los hechos"
            labelProp="descripcion"
            name="unidades_operativas"
            required={!otraOficina}
          />
        </Col>
        <Col xs={12} sm={12} md={8}>
          <Form.Item
            label=" "
            name="otra_oficina"
          >
            <Checkbox
              checked={otraOficina}
              onChange={(e) => setOtraOficina(e.target.checked)}
            >
              Otra Oficina
            </Checkbox>
          </Form.Item>
        </Col>
        {otraOficina && (
          <Col span={24}>
            <Form.Item
              label="Nombre de la oficina en donde sucedieron los hechos"
              name="oficina_de_queja"
              rules={rules.required}
            >
              <Input />
            </Form.Item>
          </Col>
        )}
        <Col span={24}>
          <Form.Item
            label="De que institución es el Trámite/Servicio o el personal con quien trató"
            name="institucion_de_queja"
            rules={rules.required}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label="Detalle de los hechos"
            name="detalle_de_hechos"
            rules={rules.required}
          >
            <Input.TextArea autoSize={{ minRows: 4, maxRows: 4 }} />
          </Form.Item>
        </Col>
      </Row>
    </HideToPreserve>
  );
};

InformacionGeneral.propTypes = {
  visible: PropTypes.bool,
};

InformacionGeneral.defaultProps = {
  visible: false,
};

export default InformacionGeneral;
